import type { FilterId } from '../../constants/filters';
import { FILTER_EXCLUSION_RULES } from '../../constants/filters/filter-exclusion-rules';
import type { FilterExclusionRule } from '../../constants/filters/filter-exclusion-rules';
import type { Filters } from '../../constants/filters/types';

/**
 * Extracts the selected values for a given filter from the store's filters map.
 * Select filters store values as `value: string[]`; boolean and other filter
 * types are not expected to appear in exclusion rules and return an empty array.
 */
const getFilterValues = (filters: Filters, filterId: string): string[] => {
	const filter = filters[filterId];
	if (filter && 'value' in filter && Array.isArray(filter.value)) {
		return filter.value;
	}
	return [];
};

/**
 * Returns the set of FilterIds that should be hidden based on the
 * currently selected product and the current filter store state.
 *
 * Evaluates all FILTER_EXCLUSION_RULES:
 * - For each rule, checks that `selectedProduct` matches (if specified)
 * - For each rule, checks that ALL `selectedFilters` conditions are satisfied
 *   (the filter has the specified value in its current selections)
 * - Aggregates `hiddenFilters` from ALL matching rules (union)
 * - Filters that are currently active (have selected values) are never hidden,
 *   even if a rule would otherwise exclude them. This prevents hiding a filter
 *   the user has already interacted with.
 *
 * @param selectedProduct - The currently selected product key, or undefined if "All"
 * @param filters - The store's filters map (Record of filterId → filter state)
 * @param rules - The exclusion rules to evaluate (defaults to FILTER_EXCLUSION_RULES, override for testing)
 */
export const getExcludedFilterIds = (
	selectedProduct: string | undefined,
	filters: Filters,
	rules: FilterExclusionRule[] = FILTER_EXCLUSION_RULES,
): Set<FilterId> => {
	const excluded = new Set<FilterId>();

	for (const rule of rules) {
		// Check product condition (if specified)
		if (rule.selectedProduct !== undefined && rule.selectedProduct !== selectedProduct) {
			continue;
		}

		// Check all filter selection conditions (AND logic — all must match)
		const allFiltersMatch = rule.selectedFilters.every(([filterId, requiredValue]) => {
			const currentValues = getFilterValues(filters, filterId);
			return currentValues.includes(requiredValue);
		});

		if (allFiltersMatch) {
			rule.hiddenFilters.forEach((f) => {
				// Never hide a filter that currently has active selections —
				// the user may have chosen a value before the exclusion rule activated.
				const filterValues = getFilterValues(filters, f);
				if (filterValues.length === 0) {
					excluded.add(f);
				}
			});
		}
	}

	return excluded;
};
